import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { toast } from 'react-toastify';
import { FaEdit } from 'react-icons/fa';
import { ChevronUp, ChevronDown, Plus } from 'lucide-react';
import { CFormSwitch } from "@coreui/react";

import styles from './PicksSections.module.scss';
import Spinner from '../../components/spinner';
import DailyLimitInput, { MIN_DAILY_LIMIT } from './dailyLimitInput';
import { useLanguage } from '../../context/languageContext';

const API = process.env.REACT_APP_API_ROOT;

// Must match the backend's REFRESH_HOURS — each divides 24, so a block always
// starts on the same IST hour.
const REFRESH_OPTIONS = [1, 2, 3, 4, 6, 8, 12, 24];
const refreshLabel = (hours) => (hours === 24 ? "Daily" : `Every ${hours}h`);

// What an automatic section picks per turn when Max in App is blank — the
// backend's MOST_RECOMMENDED_DEFAULT_LIMIT.
const AUTO_DEFAULT_LIMIT = 20;

/**
 * Controls the mobile app's "Picks" (Great Minds) tab. Four sections are seeded
 * by default (a banner + three rails); admins can also create and remove their
 * own. This page reorders sections, toggles their visibility, edits their titles
 * and members, and schedules the banner day-by-day (from the banner's edit page).
 */
function PicksSections() {
    const { adminInfo, selectedLang } = useLanguage();

    const [loading, setLoading] = useState(false);
    const [sections, setSections] = useState([]);
    const [isSavingOrder, setIsSavingOrder] = useState(false);
    // `editing`: false = closed, null = create, id string = edit that section.
    const navigate = useNavigate();
    const [pendingStatusIds, setPendingStatusIds] = useState([]);
    const [pendingCuratedIds, setPendingCuratedIds] = useState([]);
    const [pendingDisplayIds, setPendingDisplayIds] = useState([]);



    const fetchAllSections = async () => {
        setLoading(true);

        try {
            const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };
            const { data: response } = await axios.get(`${API}/api/admin/picks/sections/getall`, { headers });

            if (response.status) {
                setSections(response?.data || []);
            } else {
                toast.error(response.message || "Could not load the Picks sections.");
            }
        } catch (error) {
            console.error("fetchAllSections catch error::", error);
            toast.error("Could not load the Picks sections.");
        } finally {
            setLoading(false);
        }
    };

    /**
     * Moves a section up/down and persists the new order. The list is updated
     * first so the move feels instant, and rolled back if the request fails.
     */
    const handleMoveSection = async (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= sections.length || isSavingOrder) return;

        const reordered = [...sections];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        setSections(reordered);
        setIsSavingOrder(true);

        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.put(
                `${API}/api/admin/picks/sections/reorder`,
                { orderedSectionIds: reordered.map((section) => section.sectionId) },
                { headers }
            );

            if (response.status) {
                toast.success("Section order has been updated.");
            } else {
                // Refetch rather than restoring the snapshot: `previous` also
                // carries any status toggle made since, which would be undone.
                toast.error("Could not update the section order.");
                fetchAllSections();
            }
        } catch (error) {
            console.error("handleMoveSection catch error::", error);
            toast.error("Could not update the section order.");
            fetchAllSections();
        } finally {
            setIsSavingOrder(false);
        }
    };

    /**
     * Turns a section's hand-picked ORDER on or off. Its members don't change —
     * off, the app serves them in a per-reader, per-day order, so a static rail
     * stops showing the same faces to everyone every day.
     *
     * Same optimistic-update-and-revert shape as the visibility toggle above,
     * including the in-flight guard: rapid toggling otherwise fires overlapping
     * PUTs whose completion order isn't guaranteed.
     */
    const handleChangeSectionCurated = async (id, curated, title) => {
        if (pendingCuratedIds.includes(id)) return;
        setPendingCuratedIds((prev) => [...prev, id]);

        setSections(prevData => prevData.map(section =>
            section.sectionId === id ? { ...section, curated } : section
        ));

        const revert = () => setSections(prevData => prevData.map(section =>
            section.sectionId === id ? { ...section, curated: !curated } : section
        ));

        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.put(
                `${API}/api/admin/picks/sections/update/curated/${id}`,
                { curated },
                { headers }
            );

            if (response.status) {
                toast.success(curated
                    ? `${title} now uses your order.`
                    : `${title} is now shuffled for each reader.`);
            } else {
                revert();
                toast.error(response.message || `Could not update ${title}.`);
            }
        } catch (error) {
            console.error("handleChangeSectionCurated catch error::", error);
            revert();
            toast.error(`Could not update ${title}.`);
        } finally {
            setPendingCuratedIds((prev) => prev.filter((pendingId) => pendingId !== id));
        }
    };

    /**
     * Saves Max in App (`itemLimit`) or the refresh rate (`refreshHours`)
     * straight from the table, through the ordinary update endpoint with just
     * that field — the backend leaves everything it wasn't sent alone.
     * Optimistic, reverted on failure, one request per row at a time.
     */
    const handleUpdateDisplay = async (section, patch, successMessage) => {
        const id = section.sectionId;
        if (pendingDisplayIds.includes(id)) return;
        setPendingDisplayIds((prev) => [...prev, id]);

        const previous = Object.fromEntries(Object.keys(patch).map((k) => [k, section[k]]));
        setSections(prevData => prevData.map(s => (s.sectionId === id ? { ...s, ...patch } : s)));
        const revert = () => setSections(prevData => prevData.map(s => (s.sectionId === id ? { ...s, ...previous } : s)));

        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.put(
                `${API}/api/admin/picks/sections/update/${id}`,
                { values: patch },
                { headers }
            );

            if (response.status) {
                toast.success(successMessage);
            } else {
                revert();
                toast.error(response.message || `Could not update ${titleFor(section)}.`);
            }
        } catch (error) {
            console.error("handleUpdateDisplay catch error::", error);
            revert();
            // A 403 carries the missing permission, and a refused value (a Max
            // per day under 12) its reason under `error`; say that, not "failed".
            toast.error(
                error?.response?.data?.error
                || error?.response?.data?.message
                || `Could not update ${titleFor(section)}.`
            );
        } finally {
            setPendingDisplayIds((prev) => prev.filter((pendingId) => pendingId !== id));
        }
    };

    const handleChangeSectionStatus = async (id, newStatus, title) => {
        // Without a guard, rapid toggling fires overlapping PUTs whose
        // completion order isn't guaranteed, so the UI can end up disagreeing
        // with the server.
        if (pendingStatusIds.includes(id)) return;
        setPendingStatusIds((prev) => [...prev, id]);

        setSections(prevData => prevData.map(section =>
            section.sectionId === id ? { ...section, isActive: newStatus } : section
        ));

        // Revert just this row, leaving any other change the admin made intact.
        const revert = () => setSections(prevData => prevData.map(section =>
            section.sectionId === id ? { ...section, isActive: !newStatus } : section
        ));

        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.put(
                `${API}/api/admin/picks/sections/update/status/${id}`,
                { isActive: newStatus },
                { headers }
            );

            if (response.status) {
                toast.success(newStatus ? `${title} is now visible in the app.` : `${title} is now hidden in the app.`);
            } else {
                revert();
                toast.error(response.message || `Could not update ${title}.`);
            }
        } catch (error) {
            console.error("handleChangeSectionStatus catch error::", error);
            revert();
            toast.error(`Could not update ${title}.`);
        } finally {
            setPendingStatusIds((prev) => prev.filter((pending) => pending !== id));
        }
    };

    useEffect(() => {
        if (adminInfo && adminInfo.token) {
            fetchAllSections();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminInfo]);

    // Titles are per-language; show the one for the language being edited and
    // fall back to English so a section is never nameless in the table.
    const titleFor = (section) => {
        const translations = section.translations || [];
        const match = translations.find((t) => t.langCode === selectedLang)
            || translations.find((t) => t.langCode === "en")
            || translations[0];
        return match?.title || section.key;
    };

    return (
        <React.Fragment>
            <div className="heading">
                <div>
                    <h4 className="mb-0">Picks Sections{sections.length > 0 ? ` (${sections.length})` : ""}</h4>
                    <small className="text-muted">
                        The sections of the app's Picks tab, in the order they appear. Reorder with the
                        arrows, show or hide with the toggle, and edit to change a title or its members.
                        The app shows 12 cards on each rail and the rest under See All. Max in App caps
                        how many a reader can reach in all, rail and See All together (blank = all, at
                        least {MIN_DAILY_LIMIT}). Curated on serves your order to everyone; off, each reader
                        gets their own shuffle, which turns over at the refresh rate — so a capped rail
                        shows a different set each time.
                    </small>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-dark d-flex align-items-center gap-2"
                        onClick={() => navigate("/picks/new")}
                    >
                        <Plus size={18} /> New Section
                    </button>
                </div>
            </div>

            {loading ? (
                <Spinner />
            ) : (
                <div className={`${styles.container} table-responsive mt-1`}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={styles.order}>Order</th>
                                <th>Section</th>
                                <th>Type</th>
                                <th>Automatic / Hand-Picked</th>
                                <th>Total Count</th>
                                <th>Max in App</th>
                                <th>Refresh Rate</th>
                                <th>Visible</th>
                                <th>Curated</th>
                                <th className={styles.action}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sections.map((section, index) => (
                                <tr key={section.sectionId}>
                                    <td className={styles.order}>
                                        <div className="d-flex align-items-center gap-1">
                                            <span className={styles.position}>{index + 1}</span>
                                            <button
                                                type="button"
                                                className={styles.moveBtn}
                                                title="Move up"
                                                disabled={index === 0 || isSavingOrder}
                                                onClick={() => handleMoveSection(index, -1)}
                                            >
                                                <ChevronUp size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.moveBtn}
                                                title="Move down"
                                                disabled={index === sections.length - 1 || isSavingOrder}
                                                onClick={() => handleMoveSection(index, 1)}
                                            >
                                                <ChevronDown size={16} />
                                            </button>
                                        </div>
                                    </td>
                                    <td className={styles.sectionName}>{titleFor(section)}</td>
                                    <td className={styles.layout}>
                                        {section.layout === "banner" ? "Banner" : "Rail"}
                                    </td>
                                    <td className={styles.layout}>
                                        {section.source === "mostRecommended" ? "Automatic" : "Hand-Picked"}
                                    </td>
                                    <td
                                        className={styles.itemCount}
                                        title={section.source === "mostRecommended"
                                            ? "Books that currently qualify — the pool each refresh draws from"
                                            : undefined}
                                    >
                                        {/* null is "couldn't count", not "empty" — a dash, never 0. */}
                                        {section.itemCount === null || section.itemCount === undefined
                                            ? <span className="text-medium-emphasis">—</span>
                                            : section.itemCount}
                                    </td>
                                    <td className={styles.itemCount}>
                                        {/* A banner renders one hero card whatever the cap says. */}
                                        {section.layout === "banner" ? (
                                            <span className="text-medium-emphasis">1</span>
                                        ) : (
                                            <DailyLimitInput
                                                value={section.itemLimit}
                                                disabled={pendingDisplayIds.includes(section.sectionId)}
                                                placeholder={section.source === "mostRecommended" ? String(AUTO_DEFAULT_LIMIT) : "All"}
                                                title={`How many a reader can reach in one refresh period, rail and See All together. Blank = ${section.source === "mostRecommended" ? AUTO_DEFAULT_LIMIT : "all"}; at least ${MIN_DAILY_LIMIT}.`}
                                                onCommit={(itemLimit) => handleUpdateDisplay(
                                                    section,
                                                    { itemLimit },
                                                    itemLimit
                                                        ? `${titleFor(section)} now shows up to ${itemLimit} in the app.`
                                                        : `${titleFor(section)} now shows ${section.source === "mostRecommended" ? `up to ${AUTO_DEFAULT_LIMIT}` : "everything"}.`
                                                )}
                                            />
                                        )}
                                    </td>
                                    <td className={styles.itemCount}>
                                        {/* Rotation only happens for an automatic section or a
                                            shuffled (Curated off) one. A curated rail keeps its
                                            setting but greys it out — turning Curated off puts it
                                            to work. The banner follows its day-by-day schedule. */}
                                        {section.layout === "banner" ? (
                                            <span className="text-medium-emphasis">—</span>
                                        ) : (
                                            <select
                                                className={styles.refreshSelect}
                                                value={section.refreshHours ?? (section.source === "mostRecommended" ? 24 : 6)}
                                                disabled={
                                                    pendingDisplayIds.includes(section.sectionId)
                                                    || (section.source !== "mostRecommended" && section.curated !== false)
                                                }
                                                title={section.source !== "mostRecommended" && section.curated !== false
                                                    ? "Curated is on, so this rail always shows your order. Turn Curated off to rotate it."
                                                    : section.source === "mostRecommended"
                                                        ? "How often a fresh set is picked — the same set for every reader"
                                                        : "How often each reader's shuffle turns over"}
                                                onChange={(e) => {
                                                    const refreshHours = Number(e.target.value);
                                                    handleUpdateDisplay(
                                                        section,
                                                        { refreshHours },
                                                        `${titleFor(section)} now refreshes ${refreshHours === 24 ? "daily" : `every ${refreshHours}h`}.`
                                                    );
                                                }}
                                            >
                                                {REFRESH_OPTIONS.map((hours) => (
                                                    <option key={hours} value={hours}>{refreshLabel(hours)}</option>
                                                ))}
                                            </select>
                                        )}
                                    </td>
                                    <td className={styles.status}>
                                        <CFormSwitch
                                            size={`lg`}
                                            variant={"3d"}
                                            color={"primary"}
                                            style={{ paddingLeft: 0, width: "42px", height: "22px", cursor: "pointer" }}
                                            checked={section.isActive === true}
                                            disabled={pendingStatusIds.includes(section.sectionId)}
                                            onChange={(e) => handleChangeSectionStatus(section.sectionId, e.target.checked, titleFor(section))}
                                        />
                                    </td>
                                    <td className={styles.status}>
                                        {/* A banner renders one hero card, so there is no order to
                                            shuffle — offering the switch there only invites the
                                            question of why toggling it changes nothing. */}
                                        {/* Nor for an automatic section: it already picks a fresh
                                            set at the refresh rate, the same for every reader. */}
                                        {section.layout === "banner" || section.source === "mostRecommended" ? (
                                            <span className="text-medium-emphasis">—</span>
                                        ) : (
                                            <CFormSwitch
                                                size={`lg`}
                                                variant={"3d"}
                                                color={"primary"}
                                                style={{ paddingLeft: 0, width: "42px", height: "22px", cursor: "pointer" }}
                                                title={section.curated === false
                                                    ? "Shuffled for each reader at the refresh rate — switch on to use your order"
                                                    : "Using your order — switch off to shuffle for each reader"}
                                                checked={section.curated !== false}
                                                disabled={pendingCuratedIds.includes(section.sectionId)}
                                                onChange={(e) => handleChangeSectionCurated(section.sectionId, e.target.checked, titleFor(section))}
                                            />
                                        )}
                                    </td>
                                    <td className={styles.action}>
                                        <div className="d-inline-flex align-items-center gap-1">
                                            <div title="Edit" onClick={() => navigate(`/picks/${section.sectionId}/edit`)}>
                                                <FaEdit size={20} />
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

        </React.Fragment>
    );
}

export default PicksSections;
